import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { AlertTriangle, Clock, CreditCard, X, Sparkles } from 'lucide-react';
import { Button } from '../ui/button';
import { subscriptionAPI } from '../../lib/api';

export const TrialBanner = () => {
  const navigate = useNavigate();
  const [subscriptionStatus, setSubscriptionStatus] = useState(null);
  const [dismissed, setDismissed] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchSubscriptionStatus();
    
    // Check for trial reminders on load
    subscriptionAPI.checkTrialReminders().catch(() => {});
  }, []);

  const fetchSubscriptionStatus = async () => {
    try {
      const response = await subscriptionAPI.getStatus();
      setSubscriptionStatus(response.data);
      
      // Check if user dismissed the banner today
      const dismissedKey = `trial_banner_dismissed_${response.data?.trial_warning_level}`;
      const dismissedAt = localStorage.getItem(dismissedKey);
      if (dismissedAt) {
        const dismissedDate = new Date(dismissedAt);
        const now = new Date();
        // Reset dismissal after 24 hours
        if (now - dismissedDate > 24 * 60 * 60 * 1000) {
          localStorage.removeItem(dismissedKey);
        } else {
          setDismissed(true);
        }
      }
    } catch (error) {
      console.error('Failed to fetch subscription status:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleDismiss = () => {
    if (subscriptionStatus?.trial_warning_level) {
      localStorage.setItem(
        `trial_banner_dismissed_${subscriptionStatus.trial_warning_level}`,
        new Date().toISOString()
      );
    }
    setDismissed(true);
  };

  const handleAddPayment = () => {
    navigate('/settings?tab=subscription');
  };

  // Don't show if loading, dismissed, or not on trial
  if (loading || dismissed) return null;
  if (!subscriptionStatus) return null;
  if (subscriptionStatus.status !== 'trialing') return null;
  if (!subscriptionStatus.trial_warning_level) return null;
  if (subscriptionStatus.trial_warning_level === 'none') return null;

  const { trial_days_remaining, trial_warning_level, needs_payment_method, plan_name } = subscriptionStatus;

  // Banner styles based on warning level
  const bannerStyles = {
    info: 'bg-blue-50 border-blue-200 text-blue-800',
    warning: 'bg-amber-50 border-amber-200 text-amber-800',
    urgent: 'bg-red-50 border-red-200 text-red-800',
    expired: 'bg-red-100 border-red-300 text-red-900',
  };

  const iconStyles = {
    info: 'text-blue-500',
    warning: 'text-amber-500',
    urgent: 'text-red-500',
    expired: 'text-red-600',
  };

  const buttonStyles = {
    info: 'bg-blue-600 hover:bg-blue-700',
    warning: 'bg-amber-600 hover:bg-amber-700',
    urgent: 'bg-red-600 hover:bg-red-700',
    expired: 'bg-red-700 hover:bg-red-800',
  };

  // Get the right icon
  const Icon = trial_warning_level === 'info' ? Sparkles : 
               trial_warning_level === 'expired' ? AlertTriangle : Clock;

  // Get message based on warning level
  const getMessage = () => {
    if (trial_warning_level === 'expired') {
      return 'Your trial has ended. Add payment details to continue using LEAP Pro.';
    }
    if (trial_warning_level === 'urgent') {
      return trial_days_remaining === 0 
        ? 'Your trial expires today! Add payment details now.'
        : `Your ${plan_name} trial ends tomorrow! Add payment details to avoid service interruption.`;
    }
    if (trial_warning_level === 'warning') {
      return `Your ${plan_name} trial expires in ${trial_days_remaining} days. Add payment details to continue after your trial.`;
    }
    // info
    return `You have ${trial_days_remaining} days left in your ${plan_name} trial. Add payment details anytime to continue seamlessly.`;
  };

  return (
    <div 
      className={`border-b px-4 py-3 flex items-center justify-between gap-4 ${bannerStyles[trial_warning_level]}`}
      data-testid="trial-banner"
    >
      <div className="flex items-center gap-3">
        <Icon className={`h-5 w-5 flex-shrink-0 ${iconStyles[trial_warning_level]}`} />
        <p className="text-sm font-medium">
          {getMessage()}
        </p>
      </div>
      
      <div className="flex items-center gap-2 flex-shrink-0">
        <Button 
          size="sm" 
          onClick={handleAddPayment}
          className={`${buttonStyles[trial_warning_level]} text-white gap-1.5`}
          data-testid="add-payment-btn"
        >
          <CreditCard className="h-4 w-4" />
          {needs_payment_method ? 'Add Payment Details' : 'Upgrade Now'}
        </Button>
        
        {trial_warning_level !== 'expired' && (
          <Button
            size="sm"
            variant="ghost"
            onClick={handleDismiss}
            className="p-1.5 h-auto hover:bg-black/5"
            data-testid="dismiss-trial-banner"
          >
            <X className="h-4 w-4" />
          </Button>
        )}
      </div>
    </div>
  );
};

export default TrialBanner;
